import { inject, Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ErrorHandlingService } from './error-handling.service';
import { AmplifyControllerService } from './amplify-controller.service';
import { AppService } from './app.service';
import { UtilService } from './util.service';
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig } from '@angular/material/legacy-dialog';
import { PwAlertDialogComponent } from '../ui-test/pw-alert-dialog/pw-alert-dialog.component';
import { MessageLogout, MessageService } from './message.service';

export interface PlanDefine {
  plan: string;
  licNum: number;
  planBtId: string;
  planAmount: string;
  planCycle: string;
  planNumberOfCycles: number;
  provider: string;
}

export interface Card {
  brand: string;
  country: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

export interface Info {
  nextBillingDate: string;
  cancelAtPeriodEnd: boolean;
  status: string;
  plan: string;
  stId: string;
  planDefine: PlanDefine;
  payType: string;
  card: Card;
}

export interface BillInfoRst {
  rst: string;
  info: Info;
}

export interface IncRst {
  rst: string;
  num: number;
}

export interface iOSTransaction {
  productId: string;
  date: string;
  transactionId: string;
  state: number;
}

export interface Transaction {
  className?: string;
  transactionId: string;
  originalTransactionId?: string;
  state?: string;                 // 'approved' | 'finished' | ...
  products?: Product[];
  platform?: string;
}

export interface Receipt {
  className?: string;
  transactions: Transaction[];
  platform?: string;
  nativeData?: NativeData;
}

export interface Product {
  id: string;
}

export interface NativeData {
  appStoreReceipt?: string;
  bundleIdentifier?: string;
  bundleShortVersion?: string;
  bundleNumericVersion?: number;
  bundleSignature?: string;
}

export enum DefaultCardBrand {
  ns = 'NS'
}

export class License {
  constructor(
    public num = 0,
    public licCode = '0',
    public expiryDate = 'expired',
    public isAutoRenew = false,
    public isExpired = true,
    public planName = PlanName.freePlan,
    public type = AppService.appName,
    public productId = '',
    public autoRenewProductId = '',
    public state = '',
    public isOwner = true,
    public isAdmin = false,
    public ownerEmail = '',
    public isDisabled = false,
    public isManual = false,
  ) {
  }
}

enum LicenseStatus {
  trialing = 'trialing',
  active = 'active',
  pastDue = 'past_due',
  canceled = 'canceled',
  unpaid = 'unpaid'
}

export interface LicenseAndCard {
  license: License;
  // TODO: edu
  eduLicense: License;
  card: Card;
}

export class Card implements Card {
  constructor(
    public brand: string | DefaultCardBrand = DefaultCardBrand.ns,
    public country = '',
    public last4 = '1234',
    public exp_month = 1,
    public exp_year = 2000,
    public cardNum = `************${last4}`,
    public expDate = `${exp_month}/${exp_year}`
  ) { }
}

export enum PlanName {
  freePlan = 'Free Plan',
  premPlan = 'Premium Plan',
  plusPlan = 'Plus Plan',
  eduFreePremPlan = 'Free Edu Group Plan',
  eduTrialPremPlan = 'EDU Premium Group Plan (Free Trial)',
  eduPaidPremPlanFive = 'Edu Premium Group Plan (5 Users)',
  eduPaidPremPlanTen = 'Edu Premium Group Plan (10 Users)',
  eduPaidPremPlanThirty = 'Edu Premium Group Plan (30 Users)',
  eduPaidPremPlanFifty = 'Edu Premium Group Plan (50 Users)',
  eduPaidPremPlanDefault = 'Edu Premium Group Plan',
  eduFreePremPlanDefault = 'Free Edu Group Plan',
  eduPremGroupPlan = 'Education Premium Group Plan',
  eduPremSitePlan = 'Education Premium Site Plan',
  eduPlusGroupPlan = 'Education Plus Group Plan',
}

export class RstResult {
  constructor(
    public rst: string
  ) { }
}

const billServiceFactory = (
  http: HttpClient,
  errorHandlingService: ErrorHandlingService,
  amplifyControllerService: AmplifyControllerService,
  utilService: UtilService,
  messageService: MessageService,
) => {
  if (AppService.isIOS()) {
    console.log('bill factory: ios');
    return new IOSBillService(http, errorHandlingService, amplifyControllerService, utilService, messageService);
  } else if (AppService.isAndroid()) {
    console.log('bill factory: andriod');
    return new AndroidBillService(http, errorHandlingService, amplifyControllerService, utilService, messageService);
  } else {
    console.log('bill factory: stripe');
    return new StripeBillService(http, errorHandlingService, amplifyControllerService, utilService, messageService);
  }
};
@Injectable({
  providedIn: 'root'
})
export abstract class BillService {
  public billinfo: BillInfoRst;
  public renewrs: RstResult;
  public incrs: IncRst;
  licenseAndCard: LicenseAndCard;
  payType = '';
  hasError = false;

  public testReceipts: Receipt[] = [
    {
      "className":"Receipt",
      "transactions":[
        {
          "className":"Transaction",
          "transactionId":"appstore.application",
          "state":"finished",
          "products":[{"id":"com.naturalsoft.personalweb"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005783",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005784",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005785",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005786",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005787",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005788",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005789",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005790",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005791",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005792",
          "state":"approved",
          "products":[{"id":"Yearly_Plus_Plan"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005793",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005794",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005795",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005796",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005797",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005798",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005799",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005800",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005801",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005802",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005803",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005804",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005805",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005806",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005807",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005808",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005809",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005810",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000839005811",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        }
      ],
      "platform":"ios-appstore",
      "nativeData":{
        "appStoreReceipt":"MII/rQYJKoZIg4udUgPe",
        "bundleIdentifier":"com.naturalsoft.personalweb",
        "bundleShortVersion":"8.2",
        "bundleNumericVersion":16809984,
        "bundleSignature":"????"
      }
    },
    {
      "className":"Receipt",
      "transactions":[
        {
          "className":"Transaction",
          "transactionId":"appstore.application",
          "state":"finished",
          "products":[{"id":"com.naturalsoft.personalweb"}],
          "platform":"ios-appstore"
        },
        {
          "className":"Transaction",
          "transactionId":"2000000739005812",
          "state":"approved",
          "products":[{"id":"Plus_Monthly"}],
          "platform":"ios-appstore"
        }
      ],
      "platform":"ios-appstore",
      "nativeData":{
        "appStoreReceipt":"111",
        "bundleIdentifier":"com.naturalsoft.personalweb",
        "bundleShortVersion":"8.2",
        "bundleNumericVersion":16809984,
        "bundleSignature":"????"
      }
    }
  ];

  protected handleError(error: any): Promise<any> {
    console.error('An error occurred', error); // TODO for demo purposes only
    return Promise.reject(error.message || error);
  }

  constructor(
    protected http: HttpClient,
    protected errorHandlingService: ErrorHandlingService,
    protected amplifyControllerService: AmplifyControllerService,
    protected utilService: UtilService,
    protected messageService: MessageService
  ) { }

  // shared
  public increaseTtsUsage(charCount: string, licNum: string): Promise<any> {
    console.log('license num', licNum);
    let apiName = 'PwTts';
    if (AppService.isIOS()) {
      apiName ='IosTts';
    } else if (AppService.isAndroid()) {
      apiName ='AndroidTts';
    }
    const path = `/incr-tts`;
    const config = {
      headers: {},
      response: false, // OPTIONAL (return entire response object instead of response.data)
      queryStringParameters: {
        l: licNum,
        c: charCount
      }
    };
    return this.amplifyControllerService.getAPI()
      .then(API => {
        return API.get(apiName, path, config);
      })
      .then(response => {
        console.log('incrTts response: ', response);
        return Promise.resolve(true);
      }).catch(error => {
        console.log('incrTts err: ', error);
        if (error &&
          error.response &&
          error.response.data &&
          error.response.data.errorCode === 1005) {
          this.errorHandlingService.handleTtsDailyLimit();
        }
        else if (error &&
          error.response &&
          error.response.data &&
          error.response.data.errorCode === 'ERR_CONVERT_LIMIT') {
          this.errorHandlingService.handleTtsDailyLimit();
        }
        else {
          console.error('error: ', error);
        }
        return Promise.resolve(false);
      });
  }

  public abstract cancelAutoRenew(autoRenew, id, provider): Promise<any>;

  // shared
  protected getBillingInfoOrLicense(id: string, username: string, provider: string, resource: string): Promise<any> {
    return this.amplifyControllerService.getSession()
      .then(session => {
        if (!session) {
          return Promise.resolve(null);
        }
        const params = {
          id,
          username,
          provider
        };
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': session.getIdToken().getJwtToken()
        };

        // const url = environment.billEndpoint + '/' + resource;
        const url = this.utilService.getEndpoint('billEndpoint') + '/' + resource;

        return this.http.get(url, { params, headers }).toPromise()
          .then((response: any) => {
            // if (response['rst'] !== 'ok' && response['rst'] !== 'OK') {
            //   return Promise.resolve(null);
            // }

            this.billinfo = (response as BillInfoRst);
            console.log('this.billInfo', this.billinfo);
            // this.payType = response.planDefine.provider;
            this.payType = response.planDefine ? response.planDefine.provider : '';
            console.log('payType', this.payType);
            return Promise.resolve(response);
          })
          .catch(err => {
            console.log('err in getBillingInfoOrLicense', err);
            this.hasError = true;
            return Promise.resolve(null);
          });
      })
  }

  public getCommBillingInfo(): Promise<any> {
    return this.amplifyControllerService.getAuth()
      .then(Auth => {
        return Auth.currentSession()
          .then(session => {
            const apiName = 'CommLicense';
            const init = {
              response: false,
              header: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
              },
              queryStringParameters: {
                id: session.getIdToken().payload['sub'],
                username: session.getIdToken().payload['cognito:username'],
                provider: 'comm'
              }
            };
            return this.amplifyControllerService.getAPI()
              .then(API => {
                return API.get(apiName, '/license/', init)
                  .then(response => {
                    return response;
                  })
                  .catch(err => {
                    console.error('getBillingInfo err: ', err);
                    return Promise.reject(err);
                  });
              });
          });
      });
  }

  public upgradeToYearly(email: string) {
    return this.amplifyControllerService.getSession()
      .then(session => {
        if (!session) {
          return Promise.resolve(null);
        }

        const url = this.utilService.getEndpoint('purchaseEndpoint') + '/upgrade';
        const body = {
          email,
          priceId: 'PW_Plus_Yearly_24',
        };

        const headers = {
          'Content-Type': 'application/json',
          'Authorization': session.getIdToken().getJwtToken()
        };

        return this.http.post(url, body, { headers }).toPromise()
          .then(response => {
            console.log('upgrade response: ', response);
            if (response['ok'] != true) {
              return Promise.reject(response['error']);
            }
            return Promise.resolve(response);
          });
    });
  }

  public previewYearly(email: string) {
    return this.amplifyControllerService.getSession()
      .then(session => {
        if (!session) {
          return Promise.resolve(null);
        }

        const url = this.utilService.getEndpoint('purchaseEndpoint') + '/prorate';
        const body = {
          email,
          priceId: 'PW_Plus_Yearly_24',
        };

        const headers = {
          'Content-Type': 'application/json',
          'Authorization': session.getIdToken().getJwtToken()
        };

        return this.http.post(url, body, { headers }).toPromise()
          .then(response => {
            console.log('prorate response: ', response);
            if (response['ok'] !== true) {
              return Promise.reject(response['error']);
            }
            return Promise.resolve(response);
          });
    });
  }

  public upgradeUserAccount(email: string, liccode: string, liccodeNew: string): Promise<any> {
    // const url = environment.paymentEndpoint + '/modify';
    const url = this.utilService.getEndpoint('paymentEndpoint') + '/modify';

    const body = {
      email,
      liccodeCurr: liccode,
      liccodeNew
    };

    const headers = {
      'Content-Type': 'application/json'
    };

    return this.http.put(url, body, { headers }).toPromise()
      .then(response => {
        console.log('upgrade response: ', response);
        if (response['error'] !== '') {
          return Promise.reject(response['error']);
        }
        return Promise.resolve(response);
      });
  }

  public calculateProrate(email: string, liccode: string, liccodeNew: string): Promise<any> {
    // const url = environment.paymentEndpoint + '/preview';
    const url = this.utilService.getEndpoint('paymentEndpoint') + '/preview';

    const body = {
      email,
      liccodeCurr: liccode,
      liccodeNew
    };

    const headers = {
      'Content-Type': 'application/json'
    };

    return this.http.post(url, body, { headers }).toPromise()
      .then(response => {
        console.log('upgrade response: ', response);
        if (response['error'] !== '') {
          return Promise.reject(response['error']);
        }
        return Promise.resolve(response);
      });
  }

  public getCustomerPortalLink(email: string) {
    return this.amplifyControllerService.getSession()
      .then(session => {
        if (!session) {
          return Promise.resolve(null);
        }
        const body = {
          email,
          product: 'online'
        };
        const headers = {
          'Content-Type': 'application/json',
          Authorization: session.getIdToken().getJwtToken()
        };
    
        // const url = environment.billEndpoint + '/' + resource;
        const url = this.utilService.getEndpoint('paymentEndpoint') + '/createPortal';
    
        return this.http.post(url, body, {headers }).toPromise();
      });
  }

  public getLicenseAndCard(id: string, username: string): Promise<LicenseAndCard> {
    return Promise.resolve()
      .then(() => {
        if (username.length === 0) {
          return this.amplifyControllerService.getAuth()
            .then(Auth => {
              return Auth.currentUserInfo();
            })
            .then((userInfo) => {
              username = userInfo.username;
              console.log('cognito username', username);
              return Promise.all([this.getBillingInfoOrLicense(id, username, 'pw', 'billing'), this.getBillingInfoOrLicense(id, username, 'edu', 'license')]);
            })
            .catch(err => {
              console.log('err in getting cognito userinfo', err);
              return Promise.reject(err);
            });
        } else {
          if (AppService.isIOS() || AppService.isAndroid()) {
            const loginProvider = localStorage.getItem('LoginProvider');
            if (loginProvider && loginProvider === 'federated') {
              const federatedUserName = localStorage.getItem('CordovaFederatedUserName');
              if (federatedUserName) {
                username = federatedUserName;
              }
            }
          }
          // alert(username);
          // get billing info for pw or ios; get license for edu
          return Promise.all([this.getBillingInfoOrLicense(id, username, 'pw', 'billing'), this.getBillingInfoOrLicense(id, username, 'edu', 'license')]);
        }
      })
      .then((response) => {
        // pw or ios license and card
        console.log('billing response', response);
        const billingInfo = response[0];

        if (!billingInfo) {
          return Promise.resolve({ license: new License(), eduLicense: null, card: null });
        }

        let license = new License();
        console.log('billingInfo: ', billingInfo);
        if (billingInfo.planDefine) {
          let planDefine: PlanDefine = null;
          let planName = PlanName.freePlan;
          planDefine = billingInfo.planDefine;
          planName = this.getPlanName(planDefine.licNum);
          // alert(billingInfo.currentPeriodEnd );
          let nextBillingDate = new Date(isNaN(billingInfo.currentPeriodEnd) ? billingInfo.currentPeriodEnd : billingInfo.currentPeriodEnd * 1000);
          if ((AppService.isIOS() || AppService.isAndroid()) && isNaN(billingInfo.currentPeriodEnd)) {
            const s = billingInfo.currentPeriodEnd.replace(/-/g, '/');
            nextBillingDate = new Date(s);
          }
          console.log('nextBillingDate One',
          nextBillingDate, nextBillingDate.getFullYear(), nextBillingDate.getMonth(), nextBillingDate.getDate());
          const formattedNextBillingDate =
          this.convertDateToMonthDayYear(nextBillingDate.getFullYear(), nextBillingDate.getMonth(), nextBillingDate.getDate());
          console.log('nextBillingDate', formattedNextBillingDate);

          const isAutoRenew = !billingInfo.cancelAtPeriodEnd;

          let isExpired = true;
          if (billingInfo.status) {
            const status = billingInfo.status;
            switch (status) {
              case LicenseStatus.active:
              case LicenseStatus.trialing:
              case LicenseStatus.pastDue:
                isExpired = false;
                break;
              default:
                isExpired = false;
            }
          }
          let type = billingInfo.app;
          if (billingInfo.app) {
            type = billingInfo.app === 'stripe' && this.isLicenseTypeSupported(type) ? AppService.appName : billingInfo.app;
          }
          const productId = planDefine.planBtId ? planDefine.planBtId : '';
          const autoRenewProductId = billingInfo.autorenewproductid ? billingInfo.autorenewproductid : '';
          const subscriptionState = billingInfo.state ? billingInfo.state : '';
          license = new License(planDefine.licNum, planDefine.plan, formattedNextBillingDate, isAutoRenew, isExpired, planName, type, productId, autoRenewProductId, subscriptionState);
        }
        let card = billingInfo.card ? billingInfo.card : null;
        if (card) {
          card = new Card(card.brand, card.country, card.last4, card.exp_month, card.exp_year);
        }

        const eduInfo = response[1];
        let eduLicense = null;
        if (eduInfo && eduInfo.planDefine) {
          const eduPlanDefine = eduInfo.planDefine;

          let nextBillingDateEdu = new Date(isNaN(eduInfo.currentPeriodEnd) ? eduInfo.currentPeriodEnd : eduInfo.currentPeriodEnd * 1000);
          if (AppService.isIOS() && isNaN(eduInfo.currentPeriodEnd)) {
            const s = eduInfo.currentPeriodEnd.replace(/-/g, '/');
            nextBillingDateEdu = new Date(s);
          }

          const isExpiredEdu = eduPlanDefine.licNum === 31;

          const isAutoRenewEdu = !eduInfo.cancelAtPeriodEnd;

          console.log('eduInfo: ', eduInfo);
          const formattedNextBillingDateEdu = this.convertDateToMonthDayYear(
            nextBillingDateEdu.getFullYear(), nextBillingDateEdu.getMonth(),
            nextBillingDateEdu.getDate());

          let isDisabled = false;
          if (eduInfo.isDisabled != null) {
            isDisabled = eduInfo.isDisabled;
          }
          let isManual = false;
          if (eduInfo.isManual != null) {
            isManual = eduInfo.isManual;
          }

          let planName = this.getPlanName(eduPlanDefine.licNum);
          
          if (eduInfo.isOwner || eduInfo.isAdmin) {
            // planName = this.getPlanName(eduPlanDefine.licNum);
            if (isManual) {
              planName = planName + ' (Custom: ' + eduInfo.userCount + ' Users)' as PlanName;
            }
          }

          console.log('nextBillingDate', formattedNextBillingDateEdu);
          eduLicense = new License(
            eduPlanDefine.licNum,
            eduPlanDefine.plan,
            formattedNextBillingDateEdu,
            isAutoRenewEdu,
            isExpiredEdu,
            planName,
            '', '', '', '',
            eduInfo.isOwner,
            eduInfo.isAdmin,
            eduInfo.ownerEmail,
            isDisabled,
            isManual
          );
          console.log('eduLicense: ', eduLicense);
        }

        const licenseAndCard: LicenseAndCard = { license, eduLicense, card };
        this.licenseAndCard = licenseAndCard;
        return Promise.resolve(licenseAndCard);
      })
      .catch(err => {
        console.log('err in getLicenseAndCard', err);
        return Promise.resolve({ license: new License(), eduLicense: null, card: null });
      });
  }

  protected isLicenseTypeSupported(licenseType): any {
    if (licenseType === AppService.appName) {
      return true;
    } else {
      if (licenseType === 'stripe' && (AppService.isPW() || AppService.isSoftware())) {
        return true;
      } else {
        return false;
      }
    }
  }

  protected getPlanName(licNum: number): PlanName {
    // TODO: edu
    if (licNum <= 0) {
      return PlanName.freePlan;
    } else if (licNum === 12) {
      return PlanName.premPlan;
    } else if (licNum === 31) {
      return PlanName.eduFreePremPlan;
    } else if (licNum === 32) {
      return PlanName.eduTrialPremPlan;
    } else if (licNum === 33) {
      return PlanName.eduPaidPremPlanFive;
    } else if (licNum === 34) {
      return PlanName.eduPaidPremPlanTen;
    } else if (licNum === 35) {
      return PlanName.eduPaidPremPlanThirty;
    } else if (licNum === 36) {
      return PlanName.eduPaidPremPlanFifty;
    } else if (licNum === 37) {
      return PlanName.eduPaidPremPlanDefault;
    } else if (licNum === 38) {
      return PlanName.eduFreePremPlan;  //deprecated: PlanName.eduTieredPremPlan 
    } else if (licNum === 39) {
      return PlanName.eduFreePremPlan;  //deprecated: PlanName.eduTieredPlusPlan
    } else if (licNum === 40) {
      return PlanName.eduPremGroupPlan;
    } else if (licNum === 41) {
      return PlanName.eduPremSitePlan;
    } else if (licNum === 42) {
      return PlanName.eduPlusGroupPlan;
    }
    else if (licNum >= 13) {
      return PlanName.plusPlan;
    } else {
      return PlanName.freePlan;
    }
  }

  protected convertDateToMonthDayYear(y, m, d): string {
    if (!y || (!m && m != 0) || !d) {
      return '';
    }

    console.log('convertDateToMonthDayYear');
    const month = ['January', 'February', 'March', 'April',
      'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthDayYearFormat = month[m] + ' ' + d + ', ' + y;
    return monthDayYearFormat;
  }

  public canUseGoogleVoice(licNumStr) {
    if (licNumStr == '12' || licNumStr == '13' ||
      licNumStr == '32' || licNumStr == '33' || licNumStr == '34' ||
      licNumStr == '35' || licNumStr == '36') {
        return true;
      }

    return false;
  }
}

export class StripeBillService extends BillService {
  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Inject(ErrorHandlingService) errorHandlingService: ErrorHandlingService,
    @Inject(AmplifyControllerService) amplifyControllerService: AmplifyControllerService,
    @Inject(UtilService) utilService: UtilService,
    @Inject(MessageService) messageService: MessageService
  ) {
    super(http, errorHandlingService, amplifyControllerService, utilService, messageService);
  }

  public cancelAutoRenew(autoRenew, id, provider): Promise<any> {
    console.log(autoRenew);
    return this.amplifyControllerService.getSession()
      .then(session => {
        if (!session) {
          return Promise.resolve(null);
        }
        // const url = environment.pwUserInfo + '/subscription/cancel';
        const url = this.utilService.getEndpoint('pwUserInfo') + '/subscription/cancel';

        const params = {
          id,
          provider,
          autorenew: autoRenew
        };
        const headers = {
          'Content-Type': 'application/json',
          Authorization: session.getIdToken().getJwtToken()
        };
        // const params = new HttpParams();
        // console.log(session.getIdToken().payload.sub);
        // params.set('id', session.getIdToken().payload.sub);
        // params.set('provider', 'personal');
        // const headers = new HttpHeaders({
        //   'Authorization': session.getIdToken().getJwtToken(),
        // });
        // console.log(headers);
        const options = { params, headers };
        return this.http.delete(url, options).toPromise().catch(this.handleError);
      });
  }
}

export class IOSBillService extends BillService {
  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Inject(ErrorHandlingService) errorHandlingService: ErrorHandlingService,
    @Inject(AmplifyControllerService) amplifyControllerService: AmplifyControllerService,
    @Inject(UtilService) utilService: UtilService,
    @Inject(MessageService) messageService: MessageService
  ) {
    super(http, errorHandlingService, amplifyControllerService, utilService, messageService);
  }

  public saveReceiptInDdb(subscription_id: string, receipt, action = 'create'): Promise<any> {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: ''
    };
    // let url = environment.mobileEndpoint;
    let url = this.utilService.getEndpoint('mobileEndpoint');
    if (action === 'create') {
      url = url + '/ios';
    } else if (action === 'update') {
      url = url + '/ios-update';
    }

    return this.amplifyControllerService.getAuth()
      .then(Auth => {
        return Auth.currentUserInfo();
      })
      .then((userInfo) => {
        console.log('userInfo', userInfo);
        const email = userInfo.attributes.email;
        const transactionId = receipt['transactionId'];
        // const body = {
        //   subscription_id,
        //   receipt,
        //   email
        // };
        const body = {
            subscription_id,
            transactionId,
            email
          };
        // console.error('terry body: ', JSON.stringify(body));


        return this.amplifyControllerService.getSession()
          .then(session => {
            if (!session) {
              return Promise.resolve(null);
            }
            console.log('userInfo', session);
            headers.Authorization = session.getIdToken().getJwtToken();
            if (action === 'create') {
              return this.http.post(url, body, { headers }).toPromise()
                .then((response) => {
                  console.log('mobile endpoint response: ', response);
                  if (response['ok'] && response['ok'] === true) {

                  }
                  return Promise.resolve(null);
                })
                .catch(err => {
                  return Promise.resolve(null);
                });
            } else if (action === 'update') {
              return this.http.post(url, body, { headers }).toPromise()
                .then((response) => {
                  console.log('mobile endpoint response: ', response);
                  if (response['ok'] && response['ok'] === true) {

                  }
                  return Promise.resolve(null);
                })
                .catch(err => {
                  return Promise.resolve(null);
                });
            }

          });

      });
  }

  public getLatestTransactionId(transactions: iOSTransaction[]): string | null {
    if (transactions.length === 0) {
      return null;
    }
  
    const latestTransaction = transactions.reduce((latest, current) => {
      return new Date(latest.date) > new Date(current.date) ? latest : current;
    });
  
    return latestTransaction.transactionId;
  }

  /**
   * Retrieves the first available originalTransactionId from the given array of receipts.
   * 
   * @param receipts - An array of Receipt objects that may contain transactions.
   * @returns The first originalTransactionId found, or undefined if none exist.
   */
  public getOriginalTransactionId(receipts: Receipt[]): string | undefined {
    if (!receipts || receipts.length === 0) {
      return undefined;
    }

    for (const receipt of receipts) {
      // If transactions is missing or empty, skip this receipt.
      if (!receipt.transactions || receipt.transactions.length === 0) {
        continue;
      }

      // Go through each transaction to find one with an originalTransactionId.
      for (const tx of receipt.transactions) {
        if (tx.originalTransactionId) {
          return tx.originalTransactionId;
        }
      }
    }

    // If no originalTransactionId was found in any receipt, return undefined.
    return undefined;
  }

  public getNewestSubscriptionId(receipts: Receipt[]): string | undefined {
    const invalidIds = ['appstore.application', ''];
    let newestTransaction: Transaction | undefined;
    let maxTxNum = 0;

    for (const receipt of receipts) {
      for (const tx of receipt.transactions) {
        if (tx.transactionId && !invalidIds.includes(tx.transactionId)) {
          const extractedDigits = tx.transactionId.replace(/\D+/g, '');
          if (!extractedDigits) {
            continue;
          }
          const txNum = parseInt(extractedDigits, 10);
          if (!isNaN(txNum) && txNum > maxTxNum) {
            maxTxNum = txNum;
            newestTransaction = tx;
          }
        }
      }
    }
    if (!newestTransaction) {
      return undefined;
    }

    if (newestTransaction.originalTransactionId && 
      !invalidIds.includes(newestTransaction.originalTransactionId)) {
      return newestTransaction.originalTransactionId;
    }
  
    return newestTransaction.transactionId;
  }

  public findReceiptIndexWithAppStoreReceipt(receipts: Receipt[], txId?: string): number | undefined {
    if (txId) {
      for (let i = 0; i < receipts.length; i++) {
        const r = receipts[i];
        const hasTx = r.transactions.some(t => t.transactionId === txId);
        const hasReceipt = !!r.nativeData?.appStoreReceipt;
        if (hasTx && hasReceipt) {
          return i;
        }
      }
    }
  
    for (let i = 0; i < receipts.length; i++) {
      const r = receipts[i];
      if (r.nativeData?.appStoreReceipt) {
        return i;
      }
    }
  
    return undefined;
  }


  public restoreReceiptInDdb(receipt): Promise<any> {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: ''
    };
    // let url = environment.mobileEndpoint;
    let url = this.utilService.getEndpoint('mobileEndpoint');
    url = url + '/ios-restore';

    return this.amplifyControllerService.getAuth()
      .then(Auth => {
        return Auth.currentUserInfo();
      })
      .then((userInfo) => {
        console.log('userInfo', userInfo);
        const email = userInfo.attributes.email;
        const transactionId = receipt['transactionId'];

        // const body = {
        //   receipt,
        //   email
        // };

        const body = {
          receipt,
          email,
          transactionId
        };
        // console.error('terry restore body: ', JSON.stringify(body));

        return this.amplifyControllerService.getSession()
          .then(session => {
            if (!session) {
              return Promise.resolve(null);
            }
            console.log('userInfo', session);
            headers.Authorization = session.getIdToken().getJwtToken();
            return this.http.post(url, body, { headers }).toPromise()
              .then(
                (response) => {
                  // console.error('terry restore response: ', JSON.stringify(response));
                  console.log('mobile endpoint response: ', response);
                  // if (response['ok'] && response['ok'] == 'ok') {

                  // }
                  return Promise.resolve(response);
                },
                err => {
                  // console.error('terry restore err: ', JSON.stringify(err));
                  return Promise.resolve(null);
                })
              .catch(err => {
                // console.error('terry restore catch: ', JSON.stringify(err));
                return Promise.resolve(null);
              });
          });

      });
  }

  public cancelAutoRenew(autoRenew, id, provider): Promise<any> {
    // if(this.billinfo.info.payType === 'ios') {
    //   return Promise.resolve('ios');
    // } else if(this.billinfo.info.payType === 'stripe') {
    //   return Promise.resolve('ios');
    // }
    return Promise.resolve({ message: 'ios' });
  }
}

export class AndroidBillService extends BillService {
  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Inject(ErrorHandlingService) errorHandlingService: ErrorHandlingService,
    @Inject(AmplifyControllerService) amplifyControllerService: AmplifyControllerService,
    @Inject(UtilService) utilService: UtilService,
    @Inject(MessageService) messageService: MessageService
  ) {
    super(http, errorHandlingService, amplifyControllerService, utilService, messageService);
  }

  public cancelAutoRenew(autoRenew, id, provider): Promise<any> {
    // TODO
    return Promise.resolve();
  }

  public saveReceiptInDdb(subscriptionId: string, purchaseToken, action = 'create'): Promise<any> {
    const headers = {
      'Content-Type': 'application/json'
    };
    // let url = environment.mobileEndpoint;
    let url = this.utilService.getEndpoint('mobileEndpoint');
    if (action === 'create') {
      url = url + '/android';
    } else if (action === 'update') {
      url = url + '/android-update';
    }

    return this.amplifyControllerService.getAuth()
      .then(Auth => {
        return Auth.currentUserInfo()
      })
      .then((userInfo) => {
        console.log("userInfo", userInfo);
        const email = userInfo.attributes.email;

        const body = {
          subscriptionId,
          purchaseToken,
          email
        };

        return this.amplifyControllerService.getSession()
          .then(session => {
            if (!session) {
              return Promise.resolve(null);
            }
            console.log("userInfo", session);
            headers['Authorization'] = session.getIdToken().getJwtToken();
            if (action === 'create') {
              return this.http.post(url, body, { headers: headers }).toPromise()
                .then((response) => {
                  console.log('mobile endpoint response: ', response);
                  if (response['ok'] && response['ok'] == 'ok') {

                  }
                  return Promise.resolve(null);
                })
                .catch(err => {
                  return Promise.resolve(null);
                });
            } else if (action === 'update') {
              return this.http.post(url, body, { headers: headers }).toPromise()
                .then((response) => {
                  console.log('mobile endpoint response: ', response);
                  if (response['ok'] && response['ok'] == 'ok') {

                  }
                  return Promise.resolve(null);
                })
                .catch(err => {
                  return Promise.resolve(null);
                });
            }
          })
      })
  }


  public androidReceiptVerify(subscriptionId: string, purchaseToken): Promise<any> {
    const headers = {
      'Content-Type': 'application/json'
    };
    // let url = environment.mobileEndpoint;
    let url = this.utilService.getEndpoint('mobileEndpoint');
    url = url + '/android-validator';

    return this.amplifyControllerService.getAuth()
      .then(Auth => {
        return Auth.currentUserInfo()
      })
      .then((userInfo) => {
        console.log("userInfo", userInfo);
        const email = userInfo.attributes.email;

        const body = {
          packageName: 'com.naturalsoft.personalweb',
          subscriptionId,
          purchaseToken,
          email
        };

        return this.amplifyControllerService.getSession()
          .then(session => {
            if (!session) {
              return Promise.reject(null);
            }
            console.log("userInfo", session);
            headers['Authorization'] = session.getIdToken().getJwtToken();
            return this.http.post(url, body, { headers: headers }).toPromise()
                .then((response) => {
                  console.log('mobile endpoint response: ', response);
                  // alert(JSON.stringify(response));
                  if (response['ok'] && response['ok'] == 'ok') {
                    return Promise.resolve(null);
                  } else {
                    return Promise.reject(null);
                  }
                },
                  err => { 
                    return Promise.reject(null);
                  }
                )
                .catch(err => {
                  return Promise.reject(null);
                });
          })
      })
  }

  public restoreReceiptInDdb(subscriptionId, purchaseToken): Promise<any> {
    const headers = {
      'Content-Type': 'application/json'
    };
    // let url = environment.mobileEndpoint;
    let url = this.utilService.getEndpoint('mobileEndpoint');
    url = url + '/android-restore';

    return this.amplifyControllerService.getAuth()
      .then(Auth => {
        return Auth.currentUserInfo()
      })
      .then((userInfo) => {
        console.log("userInfo", userInfo);
        const email = userInfo.attributes.email;

        const body = {
          email,
          subscriptionId,
          purchaseToken
        };

        return this.amplifyControllerService.getSession()
          .then(session => {
            if (!session) {
              return Promise.resolve(null);
            }
            console.log("userInfo", session);
            headers['Authorization'] = session.getIdToken().getJwtToken();
            return this.http.post(url, body, { headers: headers }).toPromise()
              .then(
                (response) => {
                  console.log('mobile endpoint response: ', response);
                  // if (response['ok'] && response['ok'] == 'ok') {

                  // }
                  return Promise.resolve(response);
                },
                err => {
                  return Promise.resolve(null);
                })
              .catch(err => {
                return Promise.resolve(null);
              });
          })
      })
  }
}

export let billServiceProvider =
{
  provide: BillService,
  useFactory: billServiceFactory,
  deps: [HttpClient, ErrorHandlingService, AmplifyControllerService, UtilService, MatDialog]
};
